import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale' // Obrigatório — sem o locale o date-fns cai no inglês.

export type MonthNameStyle = 'long' | 'short'

// 'MMMM' → nome por extenso ('janeiro'); 'MMM' → abreviação sem ponto ('jan').
const PATTERN: Record<MonthNameStyle, string> = {
  long: 'MMMM',
  short: 'MMM'
}

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1)

function build(style: MonthNameStyle): string[] {
  // Ano corrente para não fixar uma data no código; dia/ano não afetam o nome do mês.
  const year = new Date().getFullYear()
  return Array.from({ length: 12 }, (_, month) => {
    const name = format(new Date(year, month, 1), PATTERN[style], { locale: ptBR })
    return style === 'long' ? capitalize(name) : name
  })
}

// Memoizado por estilo na primeira chamada; nomes de mês não mudam em runtime.
const cache: Partial<Record<MonthNameStyle, string[]>> = {}

/**
 * Returns the 12 month names in Brazilian Portuguese, as a single locale-aware source.
 *
 * - `long`: capitalized full names (`'Janeiro'`, `'Fevereiro'`, …), ready for dropdowns/labels.
 * - `short`: lowercase abbreviations without a trailing dot (`'jan'`, `'fev'`, …), for compact dates.
 *
 * Backed by `date-fns` with the `ptBR` locale and memoized on first call, so callers can
 * index the returned array directly.
 *
 * @param style - `'long'` (default) or `'short'`.
 * @returns A 12-item array indexed by month (0 = January).
 *
 * @example
 * ```ts
 * getMonthNames()[5] // 'Junho'
 * getMonthNames('short')[5] // 'jun'
 * ```
 */
export function getMonthNames(style: MonthNameStyle = 'long'): string[] {
  return (cache[style] ??= build(style))
}
